import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  CircularProgress, 
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getLogs, clearLogs } from '../../utils/logging';

const AuthDebugger: React.FC = () => {
  const { currentUser, userProfile, isLoading } = useAuth();
  const [logs, setLogs] = useState<any[]>([]);
  const [profileLoadError, setProfileLoadError] = useState<any>(null);

  useEffect(() => {
    // Get logs on mount
    setLogs(getLogs());
    
    // Check for profile load error in localStorage
    const errorJson = localStorage.getItem('profileLoadError');
    if (errorJson) {
      try {
        setProfileLoadError(JSON.parse(errorJson));
      } catch (e) {
        console.error('Failed to parse profile load error', e);
      }
    }
    
    // Refresh logs every 2 seconds
    const interval = setInterval(() => {
      setLogs(getLogs());
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  const handleClearLogs = () => {
    clearLogs();
    setLogs([]);
  };
  
  const handleClearProfileError = () => {
    localStorage.removeItem('profileLoadError');
    setProfileLoadError(null);
  };

  const formatObject = (obj: any) => {
    if (!obj) return 'null';
    
    return JSON.stringify(obj, (key, value) => {
      // Convert Date objects to ISO strings for display
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value;
    }, 2);
  };

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom>Authentication Debugger</Typography>
      <Typography color="text.secondary" paragraph>
        This page shows authentication and profile loading details to help troubleshoot issues.
      </Typography>
      
      <Paper sx={{ p: 3, my: 2 }}>
        <Typography variant="h5" gutterBottom>Authentication State</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Loading State:</Typography>
          <Typography color={isLoading ? 'warning.main' : 'success.main'}>
            {isLoading ? 'Loading...' : 'Loading Complete'}
            {isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Authentication:</Typography>
          <Chip 
            label={currentUser ? 'Authenticated' : 'Not Authenticated'} 
            color={currentUser ? 'success' : 'error'}
            sx={{ my: 1 }}
          />
          
          {currentUser && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Firebase User Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                    <ListItemText primary="User ID" secondary={currentUser.uid} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Email" secondary={currentUser.email} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Email Verified" secondary={currentUser.emailVerified ? 'Yes' : 'No'} />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Creation Time" 
                      secondary={currentUser.metadata.creationTime} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Last Sign In" 
                      secondary={currentUser.metadata.lastSignInTime} 
                    />
                  </ListItem>
                </List>
                <Box component="pre" sx={{ 
                  mt: 2,
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '0.8rem'
                }}>
                  {formatObject(currentUser)}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">User Profile:</Typography>
          <Chip 
            label={userProfile ? 'Loaded' : 'Not Loaded'} 
            color={userProfile ? 'success' : 'error'}
            sx={{ my: 1 }}
          />
          
          {userProfile ? (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Profile Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Name" 
                      secondary={`${userProfile.firstName} ${userProfile.lastName}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Role" secondary={userProfile.role} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Department" secondary={userProfile.department || 'Not set'} />
                  </ListItem>
                </List>
                <Box component="pre" sx={{ 
                  mt: 2,
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '300px',
                  fontSize: '0.8rem'
                }}>
                  {formatObject(userProfile)}
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography color="error.main">
                {currentUser ? 'User authenticated but profile not loaded!' : 'No profile (user not authenticated)'}
              </Typography>
              
              {profileLoadError && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Last Profile Load Error:</Typography>
                  <Typography variant="body2">{profileLoadError.error}</Typography>
                  <Typography variant="caption">Time: {profileLoadError.timestamp}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={handleClearProfileError}>
                      Clear Error
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3, my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Debug Logs</Typography>
          <Button variant="outlined" size="small" onClick={handleClearLogs}>
            Clear Logs
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {logs.length > 0 ? (
          <Box component="pre" sx={{ 
            p: 2, 
            bgcolor: 'grey.100', 
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '400px',
            fontSize: '0.8rem'
          }}>
            {formatObject(logs.slice(-30))} {/* Show the last 30 logs */}
          </Box>
        ) : (
          <Typography color="text.secondary">No logs recorded yet.</Typography>
        )}
      </Paper>
      
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={() => window.location.href = '/profile-troubleshooting'}
        >
          Go to Profile Troubleshooting
        </Button>
        <Button 
          variant="outlined"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Box>
    </Box>
  );
};

export default AuthDebugger; 